import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Target, Eye, Heart, Sparkles, Award, Users, ExternalLink, Mic, Newspaper, Linkedin } from 'lucide-react';

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
};

const staggerContainer = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true, margin: "-100px" },
  transition: { staggerChildren: 0.15 }
};

const staggerItem = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
};

const AboutPage = () => {
  const values = [
    {
      icon: Sparkles,
      title: 'CREATIVITY',
      description: 'We believe in the power of creative thinking to solve complex challenges and create memorable brand experiences that stand out.'
    },
    {
      icon: Target,
      title: 'DATA-DRIVEN',
      description: 'Every strategy we develop is backed by insights and analytics, ensuring measurable results and continuous optimization.'
    },
    {
      icon: Heart,
      title: 'CLIENT-CENTRIC',
      description: 'Your success is our success. We work as partners, not vendors, dedicated to understanding and achieving your unique goals.'
    }
  ];

  const whyChooseUs = [
    { icon: Award, title: 'PROVEN TRACK RECORD', desc: 'We\'ve delivered exceptional results across industries, from hospitality to e-commerce, with measurable impact.' },
    { icon: Target, title: 'TAILORED STRATEGIES', desc: 'Every brand is unique. We craft custom solutions designed specifically for your goals and audience.' },
    { icon: Users, title: 'EXPERT TEAM', desc: 'Our team combines creative talent with technical expertise to deliver comprehensive digital solutions.' }
  ];

  return (
    <div className="w-full pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" data-testid="about-hero">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-brand-blue/20 via-cyan-500/10 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-gradient-to-tl from-purple-500/10 to-transparent rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 block">ABOUT US</span>
            <h1 className="font-outfit text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] text-white mb-6 uppercase">
              ABOUT
              <span className="bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent"> ZENITH MARKETING</span>
            </h1>
            <p className="text-lg md:text-xl text-white/70 leading-relaxed">
              Where Strategy Meets Creativity
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main About Content */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* About Text */}
            <motion.div {...fadeInUp} className="max-w-xl">
              <p className="text-xl text-gray-900 leading-relaxed mb-8">
                At Zenith Marketing, we're not just another digital marketing agency. We're your partners in building brands that connect, engage, and thrive. Our services are designed to drive measurable growth, tailored to your unique goals.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Founded by <span className="font-semibold text-gray-900">Shashank Joshi</span>, Zenith Marketing was built on the belief that great marketing blends creativity with data. We don't just create campaigns; we build experiences that connect brands with their audiences and drive lasting growth.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                At Zenith Marketing, we combine creativity with data to deliver strategies that work. From scaling e-commerce businesses to helping brands establish a strong digital presence, we provide end-to-end solutions tailored to your goals.
              </p>
            </motion.div>

            {/* Founder Section */}
            <motion.div 
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-8 lg:p-10 border border-gray-200">
                <div className="relative mb-8">
                  <img 
                    src="/founder-shashank-new.jpg" 
                    alt="Shashank Joshi - Founder of Zenith Marketing"
                    data-testid="founder-photo"
                    className="w-full max-w-sm mx-auto rounded-2xl shadow-xl object-cover"
                  />
                </div>
                <div className="text-center">
                  <h3 className="font-outfit text-2xl font-bold text-gray-900 mb-1 uppercase">SHASHANK JOSHI</h3>
                  <p className="bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent font-medium mb-4">FOUNDER & CEO</p>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6 max-w-md mx-auto">
                    With over 8 years of experience in digital marketing and brand strategy, Shashank leads Zenith Marketing with a passion for helping brands achieve their full potential through innovative digital solutions.
                  </p>
                  <a 
                    href="https://in.linkedin.com/in/shashank-joshi-a05b4732"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="founder-linkedin"
                    className="inline-flex items-center bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent hover:from-cyan-400 hover:to-brand-blue font-medium transition-all"
                  >
                    <Linkedin className="w-5 h-5 mr-2 text-brand-blue" />
                    CONNECT ON LINKEDIN
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Vision */}
            <motion.div variants={staggerItem} className="bg-white p-10 md:p-12 rounded-3xl border border-gray-200 hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-brand-blue/10 to-cyan-400/10 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="text-brand-blue w-8 h-8" />
              </div>
              <h2 className="font-outfit text-3xl font-bold text-gray-900 mb-4 uppercase">OUR VISION</h2>
              <p className="text-gray-600 leading-relaxed">
                To empower businesses with innovative digital marketing solutions that deliver real results. We envision a world where every brand, regardless of size, can harness the power of digital excellence to achieve their goals and connect meaningfully with their audience.
              </p>
            </motion.div>

            {/* Mission */}
            <motion.div variants={staggerItem} className="bg-white p-10 md:p-12 rounded-3xl border border-gray-200 hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-brand-blue/10 to-cyan-400/10 rounded-2xl flex items-center justify-center mb-6">
                <Target className="text-brand-blue w-8 h-8" />
              </div>
              <h2 className="font-outfit text-3xl font-bold text-gray-900 mb-4 uppercase">OUR MISSION</h2>
              <p className="text-gray-600 leading-relaxed">
                To help brands grow through tailored digital strategies that resonate with their audience and elevate their digital presence. We are committed to delivering performance-driven results through innovative approaches, creative excellence, and data-backed insights.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 block">OUR VALUES</span>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold text-gray-900 uppercase">
              WHAT DRIVES US
            </h2>
          </motion.div>

          <motion.div {...staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, idx) => (
              <motion.div key={idx} variants={staggerItem} className="text-center p-8 md:p-10">
                <div className="w-20 h-20 bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="text-brand-blue w-10 h-10" />
                </div>
                <h3 className="font-outfit text-2xl font-bold text-gray-900 mb-4 uppercase">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Media Features */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-brand-blue/10 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 block">PRESS</span>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold text-white uppercase">
              FEATURED IN
            </h2>
          </motion.div>

          <motion.div {...staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* BrilliantRead Interview */}
            <motion.a 
              variants={staggerItem}
              href="https://www.brilliantread.com/interview-with-shashank-joshi-founder-of-zenith-marketing-india/"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white/5 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 border border-white/10"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-brand-blue to-cyan-400 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Newspaper className="text-white w-7 h-7" />
                </div>
                <div>
                  <p className="text-brand-blue text-sm font-semibold uppercase tracking-wider mb-2">BRILLIANTREAD</p>
                  <h3 className="font-outfit text-xl font-bold text-white mb-3 group-hover:bg-gradient-to-r group-hover:from-brand-blue group-hover:to-cyan-400 group-hover:bg-clip-text group-hover:text-transparent transition-all uppercase">
                    INTERVIEW WITH SHASHANK JOSHI, FOUNDER OF ZENITH MARKETING INDIA
                  </h3>
                  <p className="text-white/60 text-sm mb-4">
                    An in-depth conversation about building a digital marketing agency, entrepreneurship insights, and the future of marketing.
                  </p>
                  <span className="inline-flex items-center text-brand-blue text-sm font-medium uppercase">
                    READ FULL INTERVIEW
                    <ExternalLink className="ml-2 w-4 h-4" />
                  </span>
                </div>
              </div>
            </motion.a>

            {/* Red FM Interview */}
            <motion.div variants={staggerItem} className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mic className="text-white w-7 h-7" />
                </div>
                <div>
                  <p className="text-red-400 text-sm font-semibold uppercase tracking-wider mb-2">RED FM 93.5</p>
                  <h3 className="font-outfit text-xl font-bold text-white mb-3 uppercase">
                    RADIO INTERVIEW ON DIGITAL MARKETING & ENTREPRENEURSHIP
                  </h3>
                  <p className="text-white/60 text-sm mb-4">
                    Featured on Red FM discussing the digital marketing landscape, brand building strategies, and entrepreneurship journey.
                  </p>
                  <div className="flex items-center gap-3">
                    <img 
                      src="/redfm-interview.jpg" 
                      alt="Shashank Joshi at Red FM"
                      className="w-20 h-20 rounded-lg object-cover"
                    />
                    <span className="text-white/50 text-sm uppercase">RADIO FEATURE</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 block">WHY US</span>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold text-gray-900 uppercase">
              WHY CHOOSE ZENITH MARKETING?
            </h2>
          </motion.div>

          <motion.div {...staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whyChooseUs.map((item, idx) => (
              <motion.div key={idx} variants={staggerItem} className="bg-white p-8 rounded-2xl border border-gray-200 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-brand-blue/10 to-cyan-400/10 rounded-xl flex items-center justify-center mr-4">
                    <item.icon className="text-brand-blue w-6 h-6" />
                  </div>
                  <h3 className="font-outfit text-xl font-bold text-gray-900 uppercase">{item.title}</h3>
                </div>
                <p className="text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-brand-blue via-cyan-500 to-brand-blue relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-white/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-6 uppercase">
              READY TO PARTNER WITH US?
            </h2>
            <p className="text-white/80 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
              Let's discuss how we can help your brand reach new heights.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="about-contact-button"
                className="inline-flex items-center justify-center px-10 py-4 rounded-full bg-white text-brand-blue font-medium transition-all duration-300 hover:bg-gray-100 hover:scale-105 uppercase"
              >
                GET IN TOUCH
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/case-studies"
                data-testid="about-case-studies-button"
                className="inline-flex items-center justify-center px-10 py-4 rounded-full border-2 border-white text-white font-medium transition-all duration-300 hover:bg-white/10 uppercase"
              >
                VIEW OUR WORK
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
